/**
 * Satu-satunya pintu ke backend (ADR-043).
 *
 * Tidak ada `fetch` di komponen mana pun. Semua lewat sini, supaya empat hal punya
 * satu tempat: alamat, token, penanganan 401, dan bentuk galat.
 *
 * MASUK PAKAI JWT, BUKAN API KEY. `X-API-Key` adalah rahasia mesin (dulu milik n8n) dan
 * membuka 67 rute; kalau ia ada di JavaScript, ia ada di tab Network setiap
 * pengunjung. Browser login seperti manusia lewat POST /dashboard/login.
 */

const BASIS = '/api/v1'
const KUNCI_TOKEN = 'smova.token'
const KUNCI_SESI = 'smova.sesi'

export class GalatApi extends Error {
  constructor(pesan, { status, kode = null, mentah = null } = {}) {
    super(pesan)
    this.name = 'GalatApi'
    this.status = status
    this.kode = kode
    this.mentah = mentah
  }
}

export const sesi = {
  token: () => {
    try { return localStorage.getItem(KUNCI_TOKEN) } catch { return null }
  },
  baca: () => {
    try { return JSON.parse(localStorage.getItem(KUNCI_SESI) || 'null') } catch { return null }
  },
  simpan: (token, data) => {
    // SESI DULU, token sesudahnya — dan kalau salah satunya gagal, KEDUANYA dibuang.
    //
    // Versi lama menulis token lebih dulu lalu menelan galat apa pun. Kalau tulisan
    // kedua yang gagal (kuota penuh, mode privat), yang tertinggal adalah token TANPA
    // sesi — dan itu bukan sekadar "sesi hilang": `App.jsx` membaca peran dari sesi,
    // peran yang kosong jatuh ke cabang terakhir, dan operator Smova mendarat di
    // dashboard Tenant Marketing sambil mengirim tokennya sendiri. Gejalanya "Operator
    // Smova harus menyebut tenant lewat header X-Tenant-Id", jauh dari sebabnya.
    try {
      localStorage.setItem(KUNCI_SESI, JSON.stringify(data))
      localStorage.setItem(KUNCI_TOKEN, token)
    } catch {
      // Penyimpanan diblokir. Sesi cuma bertahan selama tab hidup — dan yang setengah
      // tertulis dibuang, karena separuh sesi lebih berbahaya daripada tidak ada sesi.
      try {
        localStorage.removeItem(KUNCI_TOKEN)
        localStorage.removeItem(KUNCI_SESI)
      } catch { /* benar-benar tidak bisa ditulis; tidak ada yang bisa dibersihkan */ }
    }
  },
  hapus: () => {
    try {
      localStorage.removeItem(KUNCI_TOKEN)
      localStorage.removeItem(KUNCI_SESI)
    } catch { /* diabaikan */ }
  },
}

// Diisi oleh <PenyediaSesi> supaya 401 bisa memulangkan orang ke layar login
// tanpa client ini perlu tahu apa-apa tentang React atau router.
let saatKedaluwarsa = null
export function pasangPenangananKedaluwarsa(fn) {
  saatKedaluwarsa = fn
}

/** Sesi ditolak server: buang yang tersimpan, lalu kabari <PenyediaSesi>. */
export function sesiDitolak() {
  sesi.hapus()
  if (saatKedaluwarsa) saatKedaluwarsa()
}

// Diisi oleh <PenyediaSesi>. Jawaban 403 bisa berarti peran orangnya sudah berubah
// sejak ia masuk: backend membaca peran dari database tiap permintaan (ADR-097), jadi
// yang basi adalah sesi di peramban ini. Sesinya disegarkan, dan halaman yang tidak lagi
// boleh dibuka dibelokkan penjaga rute (ADR-095 no. 4).
const JALUR_SAYA = '/dashboard/me'
const JALUR_MASUK = '/dashboard/login'
let saatDitolak = null
export function pasangPenangananDitolak(fn) {
  saatDitolak = fn
}

/**
 * Ubah badan galat jadi SATU KALIMAT yang bisa dibaca orang.
 *
 * `detail` bukan selalu string. Untuk galat validasi (422) FastAPI mengirimnya sebagai
 * ARRAY objek — `[{type, loc, msg, input}]` — dan `String(array)` menghasilkan
 * `[object Object]`. Itu yang muncul di layar 10 September, dan ia menutupi pesan yang
 * sebenarnya sudah menyebut persis apa yang kurang: `loc: ["query","month"]`.
 *
 * Galat yang menyembunyikan sebabnya lebih mahal daripada galat itu sendiri: yang
 * membacanya berhenti punya petunjuk, dan mulai menebak.
 *
 * `input` SENGAJA TIDAK ditampilkan. Ia memuat nilai yang dikirim — dan pada jalur
 * kredensial itu tokennya sendiri (ADR-049 menutup jalur yang sama di backend).
 */
/** `kode` mesin di badan galat (`{"detail", "kode"}`), atau null. */
export function kodeGalat(data) {
  return data && typeof data === 'object' && typeof data.kode === 'string' ? data.kode : null
}

export function pesanGalat(data, status) {
  const d = data && typeof data === 'object' ? data.detail ?? data.message : null

  if (Array.isArray(d)) {
    const bagian = d.map((x) => {
      const tempat = Array.isArray(x?.loc)
        // `loc` diawali "body"/"query"/"path" — dibuang, karena yang berguna nama
        // fieldnya, bukan di mana ia dikirim.
        ? x.loc.filter((p) => !['body', 'query', 'path'].includes(p)).join('.')
        : ''
      const pesan = x?.msg || x?.type || ''
      return tempat ? `${tempat}: ${pesan}` : pesan
    }).filter(Boolean)
    if (bagian.length) return bagian.join(' · ')
  }

  if (typeof d === 'string' && d) return d

  // BADAN YANG BUKAN JSON hampir selalu berasal dari PROXY, bukan dari aplikasi —
  // aplikasi menjawab `{"detail": "..."}` di 148 tempat. nginx menolak dengan halaman
  // HTML utuh, dan menampilkannya apa adanya berarti satu paragraf `<html><head>...`
  // di tengah layar: bukan cuma jelek, tapi menyembunyikan bahwa yang menolak bukan
  // backend. 413 punya kalimatnya sendiri karena ia satu-satunya yang bisa diperbaiki
  // orang yang membacanya — dengan mengirim berkas yang lebih kecil.
  if (status === 413) return 'Berkasnya terlalu besar untuk dikirim.'
  if (typeof data === 'string' && data && !/^\s*</.test(data)) return data
  return `HTTP ${status}`
}

/**
 * `tenant` mengirim header `X-Tenant-Id`, dan HANYA operator Smova yang boleh
 * memakainya — backend menolak 403 kalau staf tenant mengirimnya (ADR-045).
 * Itu bukan pembatasan yang perlu ditiru di sini: kalau klien ikut menyaring,
 * kesalahan pemakaian jadi diam-diam berhasil di satu sisi dan gagal di sisi lain.
 * Biarkan backend yang menolak, dengan pesannya sendiri.
 */
async function minta(metode, jalur, { body, signal, tenant } = {}) {
  const token = sesi.token()
  // TANPA TOKEN, permintaan yang butuh login tidak berangkat. Jawabannya sudah pasti 401,
  // dan selagi sesi habis halamannya tetap terpasang di bawah dialog masuk lagi: lencana
  // menu dan kotak masuk berputar tiap 30 dan 60 detik, jadi tab yang ditinggal semalam
  // mengirim ribuan permintaan yang pasti ditolak. Sesinya tetap dinyatakan habis, supaya
  // tab yang tokennya dihapus tab lain ikut memunculkan dialognya.
  if (!token && jalur !== JALUR_MASUK) {
    sesiDitolak()
    throw new GalatApi('sesi habis', { status: 401, kode: 'sesi_habis' })
  }
  const kepala = { Accept: 'application/json' }
  if (body !== undefined) kepala['Content-Type'] = 'application/json'
  if (token) kepala.Authorization = `Bearer ${token}`
  if (tenant) kepala['X-Tenant-Id'] = tenant

  let resp
  try {
    resp = await fetch(`${BASIS}${jalur}`, {
      method: metode,
      headers: kepala,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    })
  } catch (e) {
    if (e.name === 'AbortError') throw e
    // Jaringan mati, dev tidak jalan, proxy salah alamat. Bedanya penting bagi
    // yang memperbaiki: ini BUKAN backend yang menolak.
    throw new GalatApi('jaringan', { status: 0, kode: 'jaringan', mentah: e })
  }

  // 401 = token habis atau tidak sah. Bedakan dari 403 (masuk, tapi tidak berizin):
  // yang pertama minta login ulang, yang kedua tidak akan berubah dengan login ulang.
  //
  // Yang ditolak adalah TOKEN YANG DIKIRIM permintaan ini. Kalau selagi jawabannya di
  // jalan orangnya sudah masuk lagi, token yang tersimpan sudah lain, dan 401 yang
  // terlambat ini tidak boleh menghapus sesi yang baru.
  if (resp.status === 401 && jalur !== JALUR_MASUK) {
    if (sesi.token() === token) sesiDitolak()
    throw new GalatApi('sesi habis', { status: 401, kode: 'sesi_habis' })
  }
  // Galatnya tetap dilempar seperti biasa; penyegaran sesi berjalan di sampingnya.
  if (resp.status === 403 && jalur !== JALUR_SAYA && saatDitolak) saatDitolak()

  if (resp.status === 204) return null

  const teks = await resp.text()
  let data = null
  if (teks) {
    try { data = JSON.parse(teks) } catch { data = teks }
  }

  if (!resp.ok) {
    // Backend mengirim `detail` berupa PROSA Bahasa Indonesia, dan pesan ini diteruskan
    // apa adanya, tidak diterjemahkan; mencocokkan kalimat akan patah begitu kalimatnya
    // disunting. Penolakan yang perlu dibedakan layar membawa `kode` di sampingnya
    // (`core/galat.py`), mis. `jadwal_terhalang` (ADR-124).
    throw new GalatApi(pesanGalat(data, resp.status), { status: resp.status, kode: kodeGalat(data), mentah: data })
  }

  return data
}

export const api = {
  get: (jalur, opsi) => minta('GET', jalur, opsi),
  post: (jalur, body, opsi) => minta('POST', jalur, { ...opsi, body }),
  patch: (jalur, body, opsi) => minta('PATCH', jalur, { ...opsi, body }),
  // PUT dipakai dua rute yang MENGGANTI seluruh daftar, bukan menambal sebagian:
  // urutan model per peran, dan jadwal balas per fitur. Ia sempat tidak ada di sini
  // sementara `domains/tenancy/api.js` sudah memanggilnya — jadi setiap tombol di
  // layar Model dan Jadwal balas melempar "api.put is not a function", dan pesannya
  // muncul sebagai galat merah yang tidak menyebut sebab sesungguhnya.
  put: (jalur, body, opsi) => minta('PUT', jalur, { ...opsi, body }),

  /**
   * Unggah berkas. Jalurnya sendiri karena `minta` selalu JSON.stringify badannya.
   *
   * `Content-Type` SENGAJA tidak diset. multipart menuntut boundary, dan boundary itu
   * dibuat peramban saat ia menyerialkan FormData — menuliskan headernya sendiri
   * menghasilkan boundary yang tidak cocok dengan badannya, dan server menjawab 400
   * dengan pesan yang tidak menyebut sebabnya sama sekali.
   */
  async unggah(jalur, berkas, { signal } = {}) {
    const token = sesi.token()
    const data = new FormData()
    data.append('berkas', berkas)

    let resp
    try {
      resp = await fetch(`${BASIS}${jalur}`, {
        method: 'POST',
        headers: token ? { Accept: 'application/json', Authorization: `Bearer ${token}` }
                       : { Accept: 'application/json' },
        body: data,
        signal,
      })
    } catch (e) {
      if (e.name === 'AbortError') throw e
      throw new GalatApi('jaringan', { status: 0, kode: 'jaringan', mentah: e })
    }

    if (resp.status === 401) {
      sesiDitolak()
      throw new GalatApi('sesi habis', { status: 401, kode: 'sesi_habis' })
    }
    if (resp.status === 403 && saatDitolak) saatDitolak()

    const teks = await resp.text()
    let isi = null
    if (teks) {
      try { isi = JSON.parse(teks) } catch { isi = teks }
    }
    if (!resp.ok) {
      // 413 DARI NGINX, BUKAN DARI KITA — dan bentuknya HTML, bukan JSON.
      //
      // Proxy menolak badan yang lebih besar dari `client_max_body_size` SEBELUM
      // permintaannya sampai ke aplikasi, lalu membalas halaman galat HTML. Diteruskan
      // apa adanya, yang muncul di layar adalah seluruh sumber halaman itu — termasuk
      // enam baris komentar padding milik nginx. Operator kena persis ini.
      if (resp.status === 413) {
        throw new GalatApi(
          'Berkasnya terlalu besar untuk diterima server. Kecilkan dulu, atau minta '
          + 'operator menaikkan batas unggah di nginx.',
          { status: 413, kode: 'terlalu_besar', mentah: isi },
        )
      }
      // Balasan HTML dari perantara mana pun: ambil <title> kalau ada, bukan seluruh
      // dokumennya. Yang penting bagi pembaca satu kalimat, bukan sumber halaman.
      if (typeof isi === 'string' && isi.trimStart().startsWith('<')) {
        const judul = isi.match(/<title>([^<]*)<\/title>/i)?.[1]
        throw new GalatApi(judul ? `Server menolak: ${judul}` : `HTTP ${resp.status}`,
                           { status: resp.status, kode: 'html', mentah: isi })
      }
      throw new GalatApi(pesanGalat(isi, resp.status), { status: resp.status, kode: kodeGalat(isi), mentah: isi })
    }
    return isi
  },
  del: (jalur, opsi) => minta('DELETE', jalur, opsi),

  async login(username, password) {
    const data = await minta('POST', '/dashboard/login', {
      body: { username, password },
    })
    // Balasan login: { access_token, role, username }.
    // TIDAK ada tenant_id dan TIDAK ada masa berlaku — jadi klien tidak bisa tahu
    // kapan tokennya habis sebelum sebuah permintaan ditolak 401. Itu sebabnya
    // penanganan 401 di atas bukan pelengkap, melainkan satu-satunya cara tahu.
    sesi.simpan(data.access_token, { peran: data.role, nama: data.username })
    return data
  },

  /** Siapa yang sedang masuk dan perannya SEKARANG di database: `{ username, role }`. */
  saya() {
    return minta('GET', JALUR_SAYA)
  },

  keluar() {
    sesi.hapus()
  },
}

/** Susun query string tanpa kunci kosong. */
export function kueri(obj) {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(obj || {})) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}
